import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { from, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { AuthService } from '../../../core/servises/auth/auth.service';
import { catchError, filter, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import * as AuthActions from '../actions/auth.actions';
import * as AuthSelectors from '../selectors/auth.selector';
import jwtDecode from 'jwt-decode';
import { AccessTokenInterface } from '../../../core/models/access-token.interface';
import { AuthFailureResponse } from '../models/auth-response.interface';
import { LocalStorageKeys } from '../../../enums/local-storage-keys.enum';
import { permissionsHelper } from '../../../helpers/permissions.helper';
import { AppRoutesEnum } from '../../../enums/app-routes.enum';
import { Store } from '@ngrx/store';
import { OtpService } from '../../../services/otp.service';
import { UserPortfolioService } from '../../../services/user-portfolio.service';
import { SharedOrganization } from '../models/shared-organization.interface';
import { NotificationsService } from '../../../core/servises/notifications/notifications.service';
import * as OverallWorkspaceActions
  from '../../../pages/investigations/overall/state/actions/overall-workspace.actions';

const notificationTranslate = 'auth.2fa.notifications.';

@Injectable()
export class AuthEffects {
  public login$ = createEffect(
    () => {
      return this._actions.pipe(
        ofType(AuthActions.login),
        switchMap(({ username, password, otp_code }) => {
          return this._authService.login(username, password, otp_code)
            .pipe(
              // @ts-ignore
              switchMap((data) => {
                const {
                  access_token,
                  refresh_token,
                  request_password_change,
                  otp = false,
                  apply_2fa = false,
                } = data;

                if (otp) {
                  const { secret, qr } = otp;

                  return of(AuthActions.loginNeeds2FAInit({ secret, qr }));
                } else if (apply_2fa) {
                  return of(AuthActions.loginNeeds2FAVerification());
                } else {
                  return of(
                    AuthActions.loginSuccess({
                      token: access_token,
                      refreshToken: refresh_token,
                      requestPasswordChange: request_password_change,
                    }),
                    AuthActions.parseToken({ token: access_token }),
                  );
                }
              }),
              catchError((response) => {
                const error = response.error?.error;
                const { message } = <AuthFailureResponse>error;

                return of(AuthActions.loginFailure({ error: message }));
              }),
            );
        }),
      );
    },
  );

  public loginSuccess$ = createEffect(
    () =>
      this._actions.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ token, refreshToken, requestPasswordChange }) => {
          localStorage.setItem(LocalStorageKeys.Token, token);

          if (refreshToken) {
            localStorage.setItem(LocalStorageKeys.RefreshToken, refreshToken);
          }

          localStorage.removeItem(LocalStorageKeys.RequestPasswordChange);

          if (requestPasswordChange) {
            localStorage.setItem(LocalStorageKeys.RequestPasswordChange, 'changePasswordNeed');
          }
          this._router.navigateByUrl('/');
        }),
        switchMap(({ token, refreshToken }) =>
          of(
            AuthActions.setToken({ token }),
            AuthActions.setRefreshToken({ refreshToken }),
          ),
        ),
      ),
  );

  public logout$ = createEffect(
    () =>
      this._actions
        .pipe(
          ofType(AuthActions.logout),
          tap(() => {
            localStorage.removeItem(LocalStorageKeys.Token);
            localStorage.removeItem(LocalStorageKeys.RefreshToken);

            this._router.navigate(['login']);
          }),
          switchMap(() =>
            of(
              AuthActions.reset(),
              AuthActions.setToken({ token: null }),
              AuthActions.setRefreshToken({ refreshToken: null }),
              AuthActions.setUserInfo({ userInfo: null }),
              AuthActions.setOrgInfo({ orgInfo: null }),
            ),
          ),
        ),
  );

  public parseToken$ = createEffect(
    () =>
      this._actions.pipe(
        ofType(AuthActions.parseToken),
        switchMap(({ token }) => {
          const tokenInfo: AccessTokenInterface = jwtDecode(token);

          return of(
            AuthActions.setUserInfo({
              userInfo: {
                id: tokenInfo.user_id,
                name: tokenInfo.user_name,
                role: tokenInfo.role,
                sharedOrgs: tokenInfo.shared_orgs,
              },
            }),
            AuthActions.setOrgInfo({
              orgInfo: {
                id: tokenInfo.org_id,
                name: tokenInfo.org_name,
              },
            }),
            AuthActions.setToken({ token }),
            AuthActions.setSharedOrgs({ sharedOrganizations: <SharedOrganization[]>tokenInfo.shared_orgs }),
          );
        }),
      ),
  );

  public requestPasswordChange$ = createEffect(
    () => {
      return this._actions.pipe(
        ofType(AuthActions.requestPasswordChange),
        switchMap(({ oldPassword, newPassword }) => {
          return this._authService.changePassword(oldPassword, newPassword)
            .pipe(
              switchMap(() => {
                return of(
                  AuthActions.requestPasswordChangeSuccess(),
                  AuthActions.logout(),
                );
              }),
              catchError((response) => {
                const { error } = response.error;
                const { message } = <AuthFailureResponse>error;

                return of(AuthActions.requestPasswordChangeFailure({ error: message }));
              }),
            );
        }),
      );
    },
  );

  public switchOrganization$ = createEffect(
    () => {
      return this._actions.pipe(
        ofType(AuthActions.switchOrganization),
        switchMap(({ id }) => {
          return this._authService.switchOrganization(id)
            .pipe(
              switchMap((data) =>
                from(this._router.navigateByUrl(`${AppRoutesEnum.Investigations}/${AppRoutesEnum.InvestigationsOverall}`))
                  .pipe(
                    switchMap(() => {
                      const { access_token, refresh_token } = data;

                      return of(
                        AuthActions.loginSuccess({
                          token: access_token,
                        }),
                        AuthActions.switchOrganizationSuccess({
                          token: access_token,
                          refreshToken: refresh_token,
                        }),
                        AuthActions.parseToken({ token: access_token }),
                        AuthActions.updatePermissions(),
                        OverallWorkspaceActions.removeAllTabs(),
                      );
                    }),
                    tap(() => {
                      window.location.reload();
                    }),
                  ),
              ),
              catchError((response) => {
                const { error } = response.error;
                const { message } = <AuthFailureResponse>error;

                return of(AuthActions.switchOrganizationFailure({ error: message }));
              }),
            );
        }),
      );
    },
  );

  public updatePermissions$ = createEffect(() =>
    this._actions
      .pipe(
        ofType(AuthActions.updatePermissions),
        withLatestFrom(
          this._store.select(AuthSelectors.tokenSelector),
        ),
        filter(([, token]) => Boolean(token)),
        switchMap(() => this._authService.getUserData()
          .pipe(
            switchMap(userData => {
              return of(
                AuthActions.updatePermissionsSuccess({
                  permissions: permissionsHelper(userData.scopes),
                }),
                AuthActions.setUserInfo({
                  userInfo: {
                    orgEnabledOtp: userData.org_enable_otp,
                    enabledOtp: userData.enable_otp,

                  },
                }),
                AuthActions.setSharedOrgs({
                  sharedOrganizations: userData.shared_orgs,
                }),
              );
            }),
            catchError(() => of()),
          ),
        ),
      ),
  );

  public getOtpInitData$ = createEffect(() =>
    this._actions
      .pipe(
        ofType(AuthActions.getOtpInitData),
        switchMap(() => this._otpService.getOtpInitializationData()
          .pipe(
            map((data) => AuthActions.getOtpInitDataSuccess(data)),
            catchError(() => of(AuthActions.getOtpInitDataFailure())),
          ),
        ),
      ),
  );

  public toggleOtpUserState$ = createEffect(() =>
    this._actions
      .pipe(
        ofType(AuthActions.toggle2FA),
        switchMap(({ id, data }) => this._userService.updateUser(id, data)
          .pipe(
            switchMap((data) =>
              of(
                AuthActions.toggle2FASuccess(),
                AuthActions.setUserInfo({
                  userInfo: {
                    enabledOtp: data.enable_otp,
                  },
                }),
              ),
            ),
            catchError(() => of(AuthActions.toggle2FAFailure())),
          ),
        ),
      ),
  );

  public updateProfile$ = createEffect(() =>
    this._actions
      .pipe(
        ofType(AuthActions.updateProfile),
        switchMap(({ id, data }) => this._userService.updateUser(id, data)
          .pipe(
            switchMap((data) =>
              of(
                AuthActions.updateProfileSuccess(),
                AuthActions.setUserInfo({
                  userInfo: {
                    name: data.name,
                  },
                }),
              ),
            ),
            catchError(() => of(AuthActions.updateProfileFailure())),
          ),
        ),
      ),
  );

  public verify2FACode$ = createEffect(() =>
    this._actions
      .pipe(
        ofType(AuthActions.verify2FACode),
        switchMap(({ code }) => this._otpService.validateCode(code)
          .pipe(
            switchMap(() =>
              of(
                AuthActions.verify2FACodeSuccess(),
                this._notifications.handleSuccessNotification(`${notificationTranslate}enable2FA`),
                AuthActions.updatePermissions(),
              ),
            ),
            catchError(() => of(AuthActions.verify2FACodeFailure())),
          ),
        ),
      ),
  );

  public disable2FA$ = createEffect(() =>
    this._actions
      .pipe(
        ofType(AuthActions.disable2FA),
        switchMap(({ code }) => this._otpService.disable2FA(code)
          .pipe(
            switchMap(() =>
              of(
                AuthActions.disable2FASuccess(),
                this._notifications.handleSuccessNotification(`${notificationTranslate}disable2FA`),
                AuthActions.updatePermissions(),
              ),
            ),
            catchError(() => of(AuthActions.disable2FAFailure())),
          ),
        ),
      ),
  );

  constructor(
    private readonly _actions: Actions,
    private _store: Store,
    private _authService: AuthService,
    private _otpService: OtpService,
    private _userService: UserPortfolioService,
    private _router: Router,
    private _notifications: NotificationsService,
  ) {}
}
